import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import type { HiringFeedPlacement } from "@src/appV2/redesign/HiringFeed/api/hiringFeed.schema";
import { LoadingSpinner, Text } from "@src/appV2/redesign/system/ui-components";
import { useInView } from "react-intersection-observer";

import { getPayRangeText } from "../../../utils";

interface PlacementCardPayProps {
  payRange: HiringFeedPlacement["attributes"]["payRange"];
  loadingPayData: boolean;
  minMaxPayEstimate:
    | {
        min: number;
        max: number;
      }
    | undefined;
  onInView?: () => void;
}

const PAY_RATE_LABELS = {
  FROM: "Starting from",
  UP_TO: "Up to",
  TBD: "Pay rate TBD",
} as const;

export function PlacementCardPayV2(props: PlacementCardPayProps) {
  const { loadingPayData, payRange, minMaxPayEstimate, onInView } = props;

  const hasPayRateRange =
    isDefined(payRange) &&
    isDefined(payRange.minPay) &&
    isDefined(payRange.maxPay) &&
    payRange.minPay.amountInMinorUnits !== payRange.maxPay.amountInMinorUnits;

  let placementRateLabel: string | undefined;

  if (!hasPayRateRange && payRange?.minPay && !payRange?.maxPay) {
    placementRateLabel = PAY_RATE_LABELS.FROM;
  } else if (!hasPayRateRange && payRange?.maxPay) {
    placementRateLabel = PAY_RATE_LABELS.UP_TO;
  }

  const { ref } = useInView({
    triggerOnce: true,
    threshold: 0,
    onChange: (inView) => {
      if (!inView) {
        return;
      }

      if (isDefined(onInView)) {
        onInView();
      }
    },
  });

  const payRateText = getPayRangeText(minMaxPayEstimate, payRange, true);

  return (
    <Stack direction="column" spacing={1}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        flexWrap="wrap"
        flexShrink={0}
        spacing={2}
      >
        {loadingPayData ? (
          <LoadingSpinner size="small" />
        ) : (
          <>
            {placementRateLabel && !isDefined(minMaxPayEstimate) && (
              <Stack ref={ref}>
                <Text variant="body2" color={(theme) => theme.palette.intent?.disabled.text}>
                  {placementRateLabel}
                </Text>
              </Stack>
            )}
            {payRateText && (
              <Stack direction="row" alignItems="baseline">
                <Text semibold variant="body1">
                  {payRateText}&nbsp;/hr
                </Text>
              </Stack>
            )}
          </>
        )}
      </Stack>
    </Stack>
  );
}
